import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Box, Typography, Grid, useTheme, useMediaQuery } from '@mui/material/';
import Markdown from 'markdown-to-jsx';
import { Button, LibButtonProps } from '../../components/Button';
// Styles&Utils
import TelephoneAnchor from '../../utils/customHelperFunctions/TelephoneAnchor';
import styles from './styles';

export interface LibHeroBannerLanguageProps {
  richTextContent?: string;
  background?: null | {
    img: string;
    altText: string;
  };
  btnPrimary?: null | {
    label: string;
    href: string;
    colorProp: 'primary' | 'secondary' | 'tertiary' | 'text';
    target?: string;
    variant?: LibButtonProps['variant'];
  };
  btnSecondary?: null | {
    label: string;
    href: string;
    colorProp: 'primary' | 'secondary' | 'tertiary' | 'text';
    target?: string;
    variant?: LibButtonProps['variant'];
  };
}

function LibHeroBannerLanguage({
  richTextContent,
  btnPrimary,
  btnSecondary,
  background
}: LibHeroBannerLanguageProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const overrides = {
    h1: {
      component: Typography,
      props: { variant: 'h1', sx: styles.titleText }
    },
    h2: {
      component: Typography,
      props: {
        variant: 'h2',
        sx: styles.subtitleText
      }
    },
    h3: {
      component: Typography,
      props: { variant: 'body1', sx: styles.subheadingText }
    },
    p: {
      component: Typography,
      props: { variant: 'body1', sx: styles.paragraphText }
    },
    a: {
      component: TelephoneAnchor,
      props: { isSmallScreen }
    },
    strong: {
      component: Typography,
      props: { variant: 'heavy', sx: styles.strong }
    }
  };

  const getPrimaryButton = () => {
    if (btnPrimary) {
      return btnPrimary?.variant?.indexOf('switch-lang-') > -1 ? (
        <Button variant={btnPrimary.variant}>{btnPrimary?.label}</Button>
      ) : (
        <Link
          className="hero-btn"
          prefetch={false}
          href={btnPrimary?.href || ''}
          target={btnPrimary?.target || ''}
        >
          <Button
            color="primary"
            variant={btnPrimary.colorProp}
            sx={styles.heroBtn}
          >
            {btnPrimary.label}
          </Button>
        </Link>
      );
    }

    return null;
  };

  const getSecondaryButton = () => {
    if (!btnSecondary) {
      return null;
    }

    return btnSecondary?.variant?.indexOf('switch-lang-') > -1 ? (
      <Button variant={btnSecondary.variant}>{btnSecondary?.label}</Button>
    ) : (
      <Link
        className="hero-btn"
        prefetch={false}
        href={btnSecondary.href}
        target={btnSecondary.target}
      >
        <Button
          color="primary"
          variant={btnSecondary.colorProp}
          sx={styles.heroBtn}
        >
          {btnSecondary.label}
        </Button>
      </Link>
    );
  };

  return (
    <Box sx={styles.mainBox} id="heroBannerLang" component="section">
      <Grid sx={styles.bgImgNormalVar}>
        <Image
          src={background?.img || ''}
          sizes="100vw"
          alt={background?.altText || ''}
          width={0}
          height={0}
          priority
        />
      </Grid>
      <Grid container sx={styles.layoutContainer}>
        <Box sx={styles.internalLayout}>
          <Box>
            {richTextContent ? (
              <Markdown options={{ overrides }}>{richTextContent}</Markdown>
            ) : null}
          </Box>
          {(btnPrimary?.label || btnSecondary?.label) && (
            <Box sx={styles.buttonContainer}>
              {getPrimaryButton()}
              {getSecondaryButton()}
            </Box>
          )}
        </Box>
      </Grid>
    </Box>
  );
}

export const HeroBannerLanguage = React.memo(LibHeroBannerLanguage);

export default HeroBannerLanguage;
